import React from 'react';
import PropTypes from 'prop-types';
import Col from 'react-bootstrap/Col';

import MultiSelect from 'components/MultiSelect';
import {extractDynamicFieldOptions, getFieldNameKey} from './utils';

/**
 * DynamicFilter component - renders a single dynamic column filter.
 * Used by CoverageFilters to render dynamic filters in the correct order.
 *
 * Supports fieldName formats:
 *   - Single field:     "payer"
 *   - Pipe-delimited:   "payer|pbmClient"  (union of both fields' values)
 *
 * Supported column config options:
 *   - fieldName (string):       Backend field(s) to extract options from
 *   - label (string):           Filter label shown above the dropdown
 *   - placeholder (string):     Dropdown placeholder text
 *   - gridWidth (number):       Bootstrap column width
 *   - offset (number):          Bootstrap column offset
 *   - order (string[]):         Optional custom ordering of options by label
 *   - multipleSelect (bool):    Set to false to restrict to a single selection (default: true)
 */
export const DynamicFilter = ({
  column,
  coveragePositions,
  selectedDynamicFilters,
  onFilterChange,
  inProgress,
  settings,
}) => {
  // Only render if dynamicColumns is enabled and coveragePositions are available
  if (!coveragePositions || !Array.isArray(coveragePositions) || coveragePositions.length === 0) {
    return null;
  }

  const fieldName = column.fieldName;
  const fieldNameKey = getFieldNameKey(fieldName);
  let dynamicOptions = extractDynamicFieldOptions(coveragePositions, fieldName, settings?.filterLayout);

  // Apply custom ordering if specified
  if (column.order && Array.isArray(column.order) && column.order.length > 0) {
    const normalize = (str) => String(str || '').trim().toLowerCase();
    const optionsMap = new Map(dynamicOptions.map((opt) => [normalize(opt.label), opt]));
    const addedIds = new Set();

    const ordered = column.order
      .map((label) => optionsMap.get(normalize(label)))
      .filter((opt) => {
        if (opt && !addedIds.has(opt.id)) {
          addedIds.add(opt.id);
          return true;
        }
        return false;
      });

    const remaining = dynamicOptions.filter((opt) => !addedIds.has(opt.id));
    dynamicOptions = [...ordered, ...remaining];
  }

  const selectedDynamicValues = selectedDynamicFilters?.[fieldNameKey] || [];

  const handleChange = (selected) => {
    // When multipleSelect is false, keep only the most recently chosen item
    const nextSelected = column.multipleSelect === false && selected.length > 1
      ? selected.slice(-1)
      : selected;
    onFilterChange({
      selectedDynamicFilters: {
        ...(selectedDynamicFilters || {}),
        [fieldNameKey]: nextSelected,
      }
    });
  };

  return (
    <Col key={`dynamic-${fieldNameKey}`} sm={column.gridWidth} className={`offset-sm-${column.offset}`}>
      <MultiSelect
        label={column.label}
        placeholder={column.placeholder}
        options={dynamicOptions}
        selected={selectedDynamicValues}
        isFetching={inProgress}
        handleChange={handleChange}
        selectHintOnEnter={false}
        type={`dynamic-${fieldNameKey}`}
      />
    </Col>
  );
};

DynamicFilter.propTypes = {
  column: PropTypes.shape({
    filter: PropTypes.string.isRequired,
    fieldName: PropTypes.string, // Optional: supports "payer" or "payer|pbmClient"
    gridWidth: PropTypes.number,
    offset: PropTypes.number,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    order: PropTypes.array,
    multipleSelect: PropTypes.bool,
  }).isRequired,
  coveragePositions: PropTypes.array,
  selectedDynamicFilters: PropTypes.object,
  onFilterChange: PropTypes.func.isRequired,
  inProgress: PropTypes.bool.isRequired,
  settings: PropTypes.object.isRequired,
  styleSheet: PropTypes.object,
};

export default DynamicFilter;

